// /auth/callback — Figma frames 05 callback-working (33:1439) and 06
// callback-failed (33:1456): one route, two states. PRD 18 US-091 + US-092.
//
// Exchanges the magic-link token in the URL for a session
// (`GET /auth/consume`) and routes on the RFC-7807 `title` per
// progress-auth-backend.txt §"Error shapes":
//   200                          -> /dashboard/:orgSlug   (this screen)
//   410 magic_link_expired       -> /auth/link-expired    (screens-auth-a)
//   410 magic_link_consumed      -> /auth/link-expired    (screens-auth-a)
//   403 no_org_for_email         -> /no-access             (screens-shell)
//   400 magic_link_invalid, or   -> frame 06, in place     (this screen)
//     anything unrecognised (network failure, 5xx, ...)
//
// `startedRef` is load-bearing, not decoration: `app.tsx` wraps the app in
// `<StrictMode>`, so in dev React invokes this effect twice on the same
// mount to surface missing cleanup. Magic tokens are single-use — a naive
// effect would spend the token on the first invocation and read back
// `magic_link_consumed` on the second, sending a legitimate sign-in to the
// wrong screen. The ref persists across StrictMode's synthetic double
// invoke (it does not remount the component), so only the first invocation
// ever calls the API. A genuine remount (a fresh navigation back to this
// route) gets a fresh ref and is expected to fail on a spent token, same as
// any second click of an already-used link.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Icon } from "../../ui";
import { AuthShell, AuthSpinner } from "./AuthShell";
import { AuthApiError, consumeMagicToken } from "./api";
import { takeNext } from "../signin/nextUrl";

type CallbackState = "working" | "failed";

function readToken(): string | null {
  const token = new URLSearchParams(window.location.search).get("token");
  return token && token.trim() ? token.trim() : null;
}

function CallbackWorking() {
  return (
    <AuthShell>
      <AuthSpinner />
      <h1 className="maisight-invite-heading type-h3">Signing you in…</h1>
      <p className="maisight-invite-body type-bodyS">
        One moment — verifying your link.
      </p>
    </AuthShell>
  );
}

function CallbackFailed() {
  const navigate = useNavigate();
  return (
    <AuthShell>
      <span className="maisight-invite-status-icon" aria-hidden="true">
        <Icon name="alert-triangle" size={28} />
      </span>
      <h1 className="maisight-invite-heading type-h2">
        {`We couldn't verify that link`}
      </h1>
      <p className="maisight-invite-body type-bodyS">
        It may have been used already, or opened in a different browser than
        the one that requested it. Requesting a new link fixes both.
      </p>
      <div className="maisight-invite-form">
        <Button
          type="button"
          variant="primary"
          block
          onClick={() => navigate({ to: "/signin" })}
        >
          Back to sign in
        </Button>
      </div>
    </AuthShell>
  );
}

export function CallbackScreen() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>("working");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // See the module comment — StrictMode guard.
    startedRef.current = true;

    const token = readToken();
    if (!token) {
      setState("failed");
      return;
    }

    consumeMagicToken(token)
      .then((user) => {
        // A deep link followed while signed out is parked by the shell layout
        // and spent here, so the user lands where they were going rather than
        // on the overview (US-152). It cannot ride the magic link itself — the
        // round trip goes out through an inbox.
        //
        // replace: true — the token must never survive as a replayable
        // history entry (US-091). This is the only navigation on success.
        const next = takeNext();
        if (next) {
          navigate({ to: next, replace: true });
          return;
        }
        navigate({
          to: "/dashboard/$orgSlug",
          params: { orgSlug: user.org_slug },
          replace: true,
        });
      })
      .catch((err: unknown) => {
        const title =
          err instanceof AuthApiError ? err.problem?.title : undefined;
        if (title === "magic_link_expired" || title === "magic_link_consumed") {
          navigate({ to: "/auth/link-expired", replace: true });
          return;
        }
        if (title === "no_org_for_email") {
          navigate({ to: "/no-access", replace: true });
          return;
        }
        // magic_link_invalid, or anything unrecognised — render frame 06 in
        // place. Strip the token from the address bar either way, so a
        // failed exchange can't be bookmarked and replayed.
        window.history.replaceState(null, "", "/auth/callback");
        setState("failed");
      });
  }, [navigate]);

  return state === "working" ? <CallbackWorking /> : <CallbackFailed />;
}
